import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useContractFormWizard, type ContractFormState } from '@/features/contracts/hooks/useContractFormWizard';
import { useCreateContract } from '@/features/contracts/hooks/useContracts';
import { useLoadDraft, useSaveDraft, useUpdateDraft } from '@/features/contracts/hooks/useContractDrafts';
import { useSociety } from '@/features/societies/hooks/useSocieties';
import { useEffectiveConfiguration } from '@/features/societies/hooks/useSocietyConfiguration';
import { useCanMonthlyPayment } from '@/features/societies/hooks/useCanMonthlyPayment';
import { useAuth } from '@/hooks/auth/useAuth';
import { useToggleTutorials } from '@/hooks/useToggleTutorials';
import { useNotifications } from '@/contexts/NotificationContext';
import { WizardLayout } from '@/Components/shared/WizardLayout';

import { Step1Beneficiary } from './wizard-steps/Step1Beneficiary';
import { Step2VehicleInfo } from './wizard-steps/Step2VehicleInfo';
import { Step3WarrantyConfiguration } from './wizard-steps/Step3WarrantyConfiguration';
import { Step4Payment } from './wizard-steps/Step4Payment';
import { useContractTutorial } from '@/features/contracts/hooks/useContractTutorial';
import { TutorialSpotlight } from '@/features/warranties/tutorials/components/TutorialSpotlight';
import { TutorialIntro } from './TutorialIntro';
import { getFilteredTutorialSteps } from '@/features/contracts/constants/contractTutorialSteps';
import { HelpCircle, User, Car, Shield, CreditCard } from 'lucide-react';
import { PaymentLinkModal } from './PaymentLinkModal';
import { ContractConfirmationModal } from './ContractConfirmationModal';
import { VehicleCheckModal } from './VehicleCheckModal';
import { PdfPreviewPanel, usePdfPanelWidth } from '@/features/contracts/components/PdfPreviewPanel';
import { usePdfPreviewStore } from '@/features/contracts/stores/pdfPreviewStore';
import { useWarrantyDetails } from '@/features/warranties/hooks/useWarrantyDetails';
import { useAssistanceFormulas, useAssistanceArticle } from '@/features/assistance/hooks/useAssistance';
import { assistanceFormulaWording, buildAssistanceEncart } from '@/features/assistance/assistanceWording';
import { contractApi } from '@/features/contracts/api/contractApi';
import { useVendorWording } from '@/hooks/useVendorWording';
import { scrollToFirstError } from '@/utils/formUtils';
import { resolveLogoUrl } from '@/features/societies/utils/logo.utils';
import { VENDOR_TYPES } from '@/features/societies/types/society.types';
import { useWizardBrowserBack } from '@/hooks/useWizardBrowserBack';

interface WizardContainerProps {
  societyId?: string;
  onCreated?: (contractId: number) => void;
  /** Callback d'annulation (prioritaire sur navigate) */
  onCancel?: () => void;
  /** Afficher les fonctionnalités de brouillon (default true) */
  showDrafts?: boolean;
  /** Mode embarqué dans un onglet (stepper/footer sticky au lieu de fixed) */
  embedded?: boolean;
}

/**
 * Container principal du wizard de création de contrat
 */
export function WizardContainer({ societyId: societyIdProp, onCreated, onCancel, showDrafts = true, embedded }: WizardContainerProps) {
  const navigate = useNavigate();
  const { showNotification } = useNotifications();
  const createContract = useCreateContract();
  const queryClient = useQueryClient();

  // Récupérer le societyId depuis la prop ou l'URL, et returnTo/draftId depuis l'URL
  const [searchParams] = useSearchParams();
  const effectiveSocietyId = societyIdProp || searchParams.get('societyId') || undefined;
  const returnTo = searchParams.get('returnTo') || undefined;
  const draftIdFromUrl = searchParams.get('draftId') || undefined;
  const isSocietyLocked = !!effectiveSocietyId;

  const {
    formData,
    currentStep,
    touchedSteps,
    draftUuid,
    updateFormData,
    goToNextStep,
    goToPreviousStep,
    goToStep,
    toCreateDto,
    loadFromDraft,
    setDraftUuid,
    reset,
  } = useContractFormWizard(effectiveSocietyId);

  // Nettoyer le sessionStorage quand on quitte le formulaire (unmount)
  // Pattern StrictMode-safe : setTimeout annulé au remount, exécuté au vrai unmount
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => {
    clearTimeout(resetTimer.current);
    return () => {
      resetTimer.current = setTimeout(() => reset(), 0);
    };
  }, [reset]);

  // Charger le brouillon depuis l'URL
  const { data: draftFromUrl } = useLoadDraft(draftIdFromUrl);
  const [draftLoaded, setDraftLoaded] = useState(false);

  useEffect(() => {
    if (draftFromUrl && !draftLoaded) {
      loadFromDraft(draftFromUrl);
      setDraftLoaded(true);
    }
  }, [draftFromUrl, draftLoaded, loadFromDraft]);

  // Brouillon en cours de chargement : tant qu'on n'a pas hydraté formData
  // depuis le brouillon, les décisions basées sur formData (ex. ouverture
  // auto de la modale vehicle check) ne doivent pas être prises sur des
  // données vides — sinon la modale s'ouvre puis se referme (flash).
  const isDraftPending = !!draftIdFromUrl && !draftLoaded;

  // Hooks de sauvegarde de brouillon
  const saveDraft = useSaveDraft();
  const updateDraft = useUpdateDraft();
  const isSavingDraft = saveDraft.isLoading || updateDraft.isLoading;

  // Récupérer la société pour déduire isOnlinePayment
  const { data: society } = useSociety(formData.societyId);
  const wording = useVendorWording(formData.societyId);
  const { data: warranty } = useWarrantyDetails(formData.warrantyUuid);

  // Libellé de la formule d'assistance portée par la garantie sélectionnée, pour le
  // rappel de la modale de confirmation. Le parcours de création ne propose aucun
  // choix : la formule vient du paramétrage de la garantie et sera figée sur le
  // contrat par le serveur.
  const { data: assistanceFormulas } = useAssistanceFormulas(
    warranty?.assistanceFormulaId != null,
  );
  // Le contrat n'existe pas encore : la formule est lue dans le référentiel, pas
  // dans un snapshot. `assistanceFormulaWording` reçoit donc `tier`/`loanerDays`
  // directement, ce qui produit la même phrase que celle figée sur le contrat une
  // fois créé.
  // Article de l'encart : chargé uniquement quand la garantie porte une assistance.
  const { data: assistanceArticle } = useAssistanceArticle(warranty?.assistanceFormulaId != null);

  /**
   * Encart tel qu'il apparaîtra sur les CP. Composé ici depuis le RÉFÉRENTIEL —
   * le contrat n'existe pas encore — avec `tier` et `loanerDays` reconstitués comme
   * le fera le snapshot, pour que l'aperçu montre exactement le PDF à venir.
   */
  const assistanceEncart = useMemo(() => {
    if (warranty?.assistanceFormulaId == null) return null;
    const formula = (assistanceFormulas || []).find((f) => f.id === warranty.assistanceFormulaId);
    if (!formula) return null;
    return buildAssistanceEncart(
      assistanceArticle,
      { ...(formula.benefits ?? {}), tier: formula.tier, loanerDays: formula.loanerDays },
      formula.label,
    );
  }, [warranty?.assistanceFormulaId, assistanceFormulas, assistanceArticle]);

  const confirmationAssistanceSentence = useMemo(() => {
    if (warranty?.assistanceFormulaId == null) return null;
    const formula = (assistanceFormulas || []).find((f) => f.id === warranty.assistanceFormulaId);
    if (!formula) return null;
    const wording = assistanceFormulaWording(
      { tier: formula.tier, loanerDays: formula.loanerDays },
      formula.label,
    );
    return wording ? `Ce contrat dispose du service Affinicar Assist — ${wording}.` : null;
  }, [warranty?.assistanceFormulaId, assistanceFormulas]);
  const isPdfPreviewOpen = usePdfPreviewStore((s) => s.isOpen);
  const pdfPanelWidth = usePdfPanelWidth();

  // Récupérer la configuration effective (pro_v2_society_configuration)
  const { data: effectiveConfig } = useEffectiveConfiguration(formData.societyId);

  // Société à afficher sur le contrat (en-tête, identité GARANT du template).
  // Règle alignée sur le backend `resolveDisplaySociety` :
  //   si `effectiveConfig.guarantorId` est défini et pointe vers une autre
  //   société → on affiche celle-ci (la société garante) ;
  //   sinon (auto-garant ou guarantorId manquant) → la société émettrice.
  const guarantorId = effectiveConfig?.guarantorId || formData.societyId;
  const { data: guarantorSociety } = useSociety(
    guarantorId !== formData.societyId ? guarantorId : undefined,
  );
  const displaySociety = guarantorSociety ?? society;

  // showPhoneInCgpTemplate est une règle qui dépend de la société émettrice
  // (pas du garant). On continue à le lire sur `effectiveConfig` (société
  // sélectionnée dans le wizard), cohérent avec le backend.
  const showPhoneInCgp = effectiveConfig?.showPhoneInCgpTemplate;

  // Type effectif pour la preview PDF :
  // - tant que la garantie n'est pas chargée, déduire depuis vendorType
  //   (Prestataire → 1, Garant → 0) pour afficher le bon template dès Step 1.
  // - dès qu'une garantie est sélectionnée, sa propre warrantyType prend la main
  //   (cas Both : l'utilisateur peut créer aussi bien Garantie que Prestation).
  // Fallback : vendorType de la société émettrice.
  const effectiveWarrantyType = useMemo(() => {
    if (warranty?.warrantyType !== undefined) return warranty.warrantyType;
    const fallbackVendorType = society?.vendorType;
    if (fallbackVendorType === VENDOR_TYPES.PRESTATAIRE) return 1;
    if (fallbackVendorType === VENDOR_TYPES.GARANT) return 0;
    return undefined;
  }, [warranty?.warrantyType, society?.vendorType]);

  // État de la modale de vérification véhicule
  const [showVehicleCheckModal, setShowVehicleCheckModal] = useState(false);
  const [isUploadingVehicleCheck, setIsUploadingVehicleCheck] = useState(false);
  // Action en attente après confirmation du vehicle check (intégration tutoriel)
  const [pendingAction, setPendingAction] = useState<'start_tutorial' | 'skip_tutorial' | 'continue_tutorial' | null>(null);
  // Considérer le check comme déjà fait si le brouillon contenait une URL
  const vehicleCheckDone = !!formData.vehicleCheckReportUrl;
  // Distinguer ouverture au chargement vs clic sur "Suivant"
  const vehicleCheckOpenedOnLoad = useRef(false);

  // État de validation du step courant (remonté par chaque step)
  const [canProceed, setCanProceed] = useState(false);
  // Afficher les erreurs de validation (activé quand on clique "Suivant" sans validation)
  const [showErrors, setShowErrors] = useState(false);

  // Paiement en ligne disponible
  const hasOnlinePayment = effectiveConfig?.onlinePayment === 2 && !!effectiveConfig?.stripeAccountId;

  // Texte descriptif du mode de paiement (réplique la logique API getPaymentMethodText)
  const paymentMethodText = useMemo(() => {
    const term = warranty?.warrantyType === 1 ? 'Prestation' : 'Garantie';
    if (formData.paymentType === 2) {
      return `Paiement échelonné mensuel applicable pendant la durée de la ${term} réalisé en ligne, via le lien fourni au Bénéficiaire lors de la conclusion du Contrat. En remplissant et validant le formulaire communiqué, le Bénéficiaire accepte de payer l'ensemble des mensualités correspondant au prix de la ${term} et le mandat de prélèvement associé.`;
    }
    if (formData.paymentType === 1 && hasOnlinePayment) {
      return `Paiement unique réalisé en ligne, via le lien fourni au Bénéficiaire lors de la conclusion du Contrat. En remplissant et validant le formulaire communiqué, le Bénéficiaire accepte de payer le prix total de la ${term}.`;
    }
    return '';
  }, [formData.paymentType, hasOnlinePayment, warranty?.warrantyType]);
  const canMonthlyPayment = useCanMonthlyPayment(formData.societyId);

  // Step 4 (Payment) is shown when there are payment options to configure
  const showStep4 = !formData.includedOption && formData.sellingPrice > 0 && (hasOnlinePayment || (canMonthlyPayment && warranty?.isMensualisable));
  const totalSteps = showStep4 ? 4 : 3;

  const STEP_LABELS = useMemo(() => {
    const steps = [
      { number: 1, label: 'Bénéficiaire', icon: <User className="tw-w-6 tw-h-6" /> },
      { number: 2, label: 'Véhicule', icon: <Car className="tw-w-6 tw-h-6" /> },
      { number: 3, label: wording.singular, icon: <Shield className="tw-w-6 tw-h-6" /> },
    ];
    if (showStep4) {
      steps.push({ number: 4, label: 'Paiement', icon: <CreditCard className="tw-w-6 tw-h-6" /> });
    }
    return steps;
  }, [wording, showStep4]);

  // Guard: if Step 4 disappears (e.g. user checks "included"), go back to step 3
  useEffect(() => {
    if (currentStep > totalSteps) {
      goToStep(totalSteps);
    }
  }, [currentStep, totalSteps, goToStep]);

  // État de la modale de partage du lien de paiement (mensuel)
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentLink, setPaymentLink] = useState<string | undefined>();
  const [signatureLink, setSignatureLink] = useState<string | undefined>();

  // État de la modale de confirmation (contrats sans paiement Stripe)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  // Ouvrir la modale dès le chargement si le vehicle check est obligatoire et pas encore fait
  useEffect(() => {
    // Attendre l'hydratation du brouillon : sinon on décide sur formData vide
    // (vehicleCheckReportUrl pas encore chargé) → modale ouverte puis refermée.
    if (isDraftPending) return;
    if (effectiveConfig?.mandatoryVehicleCheck && !vehicleCheckDone) {
      vehicleCheckOpenedOnLoad.current = true;
      setShowVehicleCheckModal(true);
    } else if (vehicleCheckDone) {
      setShowVehicleCheckModal(false);
    }
  }, [effectiveConfig?.mandatoryVehicleCheck, vehicleCheckDone, isDraftPending]);

  // Détecter le type d'utilisateur et la préférence tutoriel
  const { user } = useAuth();
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);
  const isTutorialEnabled = isExternalUser && user?.showTutorials !== false;
  const { enableTutorials, disableTutorials } = useToggleTutorials();

  // Hook du tutoriel
  const {
    state: tutorialState,
    startTutorial,
    nextStep: nextTutorialStep,
    previousStep: previousTutorialStep,
    skipTutorial,
    completeTutorial,
    restartAtStep,
    showIntro,
  } = useContractTutorial();

  // Obtenir les étapes du tutoriel filtrées
  const tutorialSteps = useMemo(() => {
    const baseSteps = getFilteredTutorialSteps(isExternalUser, society?.vendorType);
    // Filtrer les steps dont le fieldRef retourne null (blocs conditionnels non affichés)
    return baseSteps.filter((step) => {
      const fieldRef = typeof step.fieldRef === 'function'
        ? step.fieldRef(formData)
        : step.fieldRef;
      return fieldRef !== null && fieldRef !== undefined;
    });
  }, [isExternalUser, formData, society?.vendorType]);

  // Afficher l'intro du tutoriel à chaque visite du wizard (si activé dans le profil)
  // Les flags hasBeenCompleted/hasBeenSkipped ne sont valables que pour la session en cours
  useEffect(() => {
    if (isTutorialEnabled && !tutorialState.hasBeenCompleted && !tutorialState.hasBeenSkipped && !tutorialState.showIntro && !tutorialState.isActive) {
      // Délai pour laisser le DOM se charger
      const timer = setTimeout(() => {
        showIntro();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isTutorialEnabled, tutorialState.hasBeenCompleted, tutorialState.hasBeenSkipped, tutorialState.showIntro, tutorialState.isActive, showIntro]);

  // Étape actuelle du tutoriel
  const currentTutorialStep = useMemo(() => {
    if (!tutorialState.isActive) return null;
    return tutorialSteps[tutorialState.currentStepIndex];
  }, [tutorialState.isActive, tutorialState.currentStepIndex, tutorialSteps]);

  // Vérifier si le champ actuel du tutoriel est valide
  const isTutorialFieldValid = useMemo(() => {
    if (!currentTutorialStep) return false;
    return currentTutorialStep.validation(formData);
  }, [currentTutorialStep, formData]);

  // Calculer l'index du premier step du wizardStep actuel (pour bouton Aide)
  const firstStepIndexForCurrentWizard = useMemo(() => {
    return tutorialSteps.findIndex(step => step.wizardStep === currentStep);
  }, [tutorialSteps, currentStep]);

  // Wrapper goToStep : synchronise le tutoriel quand l'utilisateur navigue manuellement
  const handleGoToStep = useCallback((step: number) => {
    goToStep(step);
    if (tutorialState.isActive) {
      const firstIndex = tutorialSteps.findIndex(s => s.wizardStep === step);
      if (firstIndex !== -1) {
        restartAtStep(firstIndex);
      }
    }
  }, [goToStep, tutorialState.isActive, tutorialSteps, restartAtStep]);

  // Gérer le passage à l'étape suivante du tutoriel
  const handleNextTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex >= tutorialSteps.length - 1) {
      // Fin du tutoriel : rester à l'étape 3 (garantie)
      disableTutorials();
      completeTutorial();
      goToStep(3);
      showNotification('success', 'Tutoriel terminé ! Vous pouvez maintenant créer votre contrat.');
    } else {
      // Vérifier si on passe du wizardStep 1 au wizardStep 2
      const nextStep = tutorialSteps[tutorialState.currentStepIndex + 1];
      if (nextStep && nextStep.wizardStep === 2 && currentStep === 1) {
        // Intercept : vérification véhicule obligatoire avant de passer à l'étape 2
        if (effectiveConfig?.mandatoryVehicleCheck && !vehicleCheckDone) {
          setPendingAction('continue_tutorial');
          setShowVehicleCheckModal(true);
          return;
        }
      }

      nextTutorialStep();

      // Changer de wizard step si nécessaire
      if (nextStep && nextStep.wizardStep !== currentStep) {
        goToStep(nextStep.wizardStep);
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    disableTutorials,
    completeTutorial,
    nextTutorialStep,
    currentStep,
    goToStep,
    showNotification,
    effectiveConfig?.mandatoryVehicleCheck,
    vehicleCheckDone,
  ]);

  // Gérer le retour à l'étape précédente du tutoriel
  const handlePreviousTutorialStep = useCallback(() => {
    if (tutorialState.currentStepIndex > 0) {
      previousTutorialStep();

      // Changer de wizard step si nécessaire
      const prevStep = tutorialSteps[tutorialState.currentStepIndex - 1];
      if (prevStep && prevStep.wizardStep !== currentStep) {
        goToStep(prevStep.wizardStep);
      }
    }
  }, [
    tutorialState.currentStepIndex,
    tutorialSteps,
    previousTutorialStep,
    currentStep,
    goToStep,
  ]);

  // Utiliser touchedSteps pour permettre la navigation vers toutes les steps déjà visitées
  // (même si on revient en arrière)

  // Ouvre la modale de partage du lien de paiement (mensuel)
  const handleOpenPaymentModal = useCallback(() => {
    setPaymentLink(undefined);
    setSignatureLink(undefined);
    setShowPaymentModal(true);
  }, []);

  // Confirmation depuis la modale sans Stripe : met à jour signatureType + coordonnées puis crée le contrat
  const handleConfirmationModalConfirm = useCallback(async (signatureType: 1 | 2, email?: string, phone?: string) => {
    try {
      const updates: Partial<ContractFormState> = { signatureType };
      if (email !== undefined || phone !== undefined) {
        updates.beneficiary = {
          ...formData.beneficiary,
          ...(email !== undefined && { email }),
          ...(phone !== undefined && { phoneNumber: phone }),
        };
      }
      updateFormData(updates);

      const dto = toCreateDto(false);
      dto.signatureType = signatureType;
      if (email !== undefined) dto.beneficiary = { ...dto.beneficiary, email };
      if (phone !== undefined) dto.beneficiary = { ...dto.beneficiary, phoneNumber: phone };

      const result = await createContract.mutateAsync(dto);

      if (draftUuid) {
        queryClient.invalidateQueries({ queryKey: ['contract-drafts'] });
      }
      if (effectiveSocietyId) {
        queryClient.invalidateQueries({
          queryKey: ['society-hierarchy-contracts', effectiveSocietyId],
        });
      }

      setShowConfirmationModal(false);
      if (onCreated) {
        onCreated(result.id);
      } else if (returnTo) {
        navigate(returnTo);
      } else {
        navigate(`/contracts/${result.id}`);
      }
    } catch (error) {
      // Erreur gérée par le hook useCreateContract
    }
  }, [formData, toCreateDto, createContract, navigate, returnTo, effectiveSocietyId, queryClient, updateFormData, draftUuid, onCreated]);

  // Confirmation depuis la modale : met à jour l'email/phone/signatureType du bénéficiaire puis crée le contrat
  const handlePaymentModalConfirm = useCallback(async (email: string, phone: string, signatureType: 1 | 2, skipNotifications: boolean, paymentExpirationHours?: number) => {
    try {
      // Mettre à jour le bénéficiaire et le type de signature avec les valeurs de la modale
      updateFormData({
        beneficiary: {
          ...formData.beneficiary,
          email,
          phoneNumber: phone,
        },
        signatureType,
      });

      // Construire le DTO avec les valeurs à jour
      // (on override manuellement car le state React n'est pas encore mis à jour)
      const dto = toCreateDto(effectiveConfig?.onlinePayment === 2 && !!effectiveConfig?.stripeAccountId);
      dto.beneficiary = { ...dto.beneficiary, email, phoneNumber: phone };
      dto.signatureType = signatureType;
      if (skipNotifications) {
        dto.skipNotifications = true;
      }
      if (paymentExpirationHours) {
        dto.paymentExpirationHours = paymentExpirationHours;
      }

      const result = await createContract.mutateAsync(dto);

      // Invalider le cache des brouillons
      if (draftUuid) {
        queryClient.invalidateQueries({ queryKey: ['contract-drafts'] });
      }

      // Invalider le cache
      if (effectiveSocietyId) {
        queryClient.invalidateQueries({
          queryKey: ['society-hierarchy-contracts', effectiveSocietyId],
        });
      }

      // Si l'API retourne un paymentLink, afficher le QR code dans la modale
      if (result.paymentLink) {
        setPaymentLink(result.paymentLink);
        setSignatureLink(result.signatureLink);
      } else {
        // Pas de lien de paiement dans la réponse : fermer et rediriger
        setShowPaymentModal(false);
        if (onCreated) {
          onCreated(result.id);
        } else if (returnTo) {
          navigate(returnTo);
        } else {
          navigate(`/contracts/${result.id}`);
        }
      }
    } catch (error) {
      // Erreur gérée par le hook useCreateContract (notification d'erreur)
    }
  }, [formData, toCreateDto, society, createContract, navigate, returnTo, effectiveSocietyId, queryClient, updateFormData, draftUuid, onCreated]);

  // Sauvegarder le brouillon puis rediriger vers la liste des brouillons
  const handleSaveDraft = useCallback(async () => {
    if (draftUuid) {
      await updateDraft.mutateAsync({
        uuid: draftUuid,
        formData: formData as Record<string, any>,
        currentStep,
      });
    } else {
      const result = await saveDraft.mutateAsync({
        formData: formData as Record<string, any>,
        currentStep,
      });
      setDraftUuid(result.uuid);
    }
    navigate('/contracts?tab=drafts');
  }, [draftUuid, formData, currentStep, saveDraft, updateDraft, setDraftUuid, navigate]);

  // Intercepter la navigation Step 1 → Step 2 pour le vehicle check
  const handleStep1Next = useCallback(() => {
    if (effectiveConfig?.mandatoryVehicleCheck && !vehicleCheckDone) {
      setShowVehicleCheckModal(true);
      return;
    }
    goToNextStep();
  }, [effectiveConfig?.mandatoryVehicleCheck, vehicleCheckDone, goToNextStep]);

  // Confirmation du vehicle check : upload vers S3 puis action selon le contexte
  const handleVehicleCheckConfirm = useCallback(async (file: File) => {
    setIsUploadingVehicleCheck(true);
    try {
      const { fileUrl } = await contractApi.uploadVehicleCheckReport(file);
      updateFormData({ vehicleCheckReportUrl: fileUrl });
      setShowVehicleCheckModal(false);

      // Exécuter l'action en attente selon le contexte
      if (pendingAction === 'start_tutorial') {
        startTutorial(firstStepIndexForCurrentWizard !== -1 ? firstStepIndexForCurrentWizard : 0);
      } else if (pendingAction === 'skip_tutorial') {
        skipTutorial();
      } else if (pendingAction === 'continue_tutorial') {
        // Continuer le tutoriel : avancer d'un step tutoriel + naviguer vers wizardStep 2
        nextTutorialStep();
        goToStep(2);
      } else if (vehicleCheckOpenedOnLoad.current) {
        // Ouvert au chargement : rester sur le Step 1 (l'utilisateur n'a pas cliqué "Suivant")
        vehicleCheckOpenedOnLoad.current = false;
      } else {
        // Flux normal (non-tutoriel, clic "Suivant") : passer à l'étape suivante
        goToNextStep();
      }
      setPendingAction(null);
    } catch (error) {
      showNotification('error', 'Erreur lors de l\'upload du rapport de vérification');
    } finally {
      setIsUploadingVehicleCheck(false);
    }
  }, [updateFormData, goToNextStep, goToStep, showNotification, pendingAction, startTutorial, skipTutorial, nextTutorialStep, firstStepIndexForCurrentWizard]);

  // Logique du bouton suivant selon le step
  const handleNext = useCallback(() => {
    if (!canProceed) {
      setShowErrors(true);
      scrollToFirstError();
      return;
    }
    setShowErrors(false);
    if (currentStep === 1) {
      handleStep1Next();
    } else if (currentStep === 2) {
      goToNextStep();
    } else if (currentStep === 3) {
      if (showStep4) {
        // Go to Step 4 (Payment)
        goToNextStep();
      } else {
        // Dernier step sans paiement Stripe → modale de confirmation
        setShowConfirmationModal(true);
      }
    } else if (currentStep === 4) {
      const needsPaymentModal =
        formData.paymentType === 2 || (formData.paymentType === 1 && hasOnlinePayment && formData.paymentMethod !== 3);
      if (needsPaymentModal) {
        handleOpenPaymentModal();
      } else {
        // Step 4 sans paiement en ligne (ex: point de vente) → modale de confirmation
        setShowConfirmationModal(true);
      }
    }
  }, [canProceed, currentStep, handleStep1Next, goToNextStep, showStep4, formData.paymentType, formData.paymentMethod, hasOnlinePayment, handleOpenPaymentModal]);

  // Label du bouton suivant selon le step
  const nextLabel = useMemo(() => {
    const isLastStep = showStep4 ? currentStep === 4 : currentStep === 3;
    if (isLastStep) {
      return 'Finaliser le contrat';
    }
    return 'Suivant';
  }, [currentStep, showStep4]);

  // Reset showErrors quand on change de step
  useEffect(() => {
    setShowErrors(false);
  }, [currentStep]);

  // Protection bouton retour navigateur (active uniquement à partir du step 2) :
  // - back en step 2+ → retour au step précédent, données préservées
  // - step 1 → comportement navigateur natif (l'utilisateur peut quitter librement)
  // - beforeunload (refresh / fermeture tab) → alerte navigateur native (step 2+)
  useWizardBrowserBack({
    currentStep,
    goToPreviousStep,
    enabled: currentStep > 1,
    beforeUnloadEnabled: !createContract.isLoading,
  });

  const renderCurrentStep = () => {
    const commonProps = {
      formData,
      updateFormData,
      onCanProceedChange: setCanProceed,
      showErrors,
    };

    switch (currentStep) {
      case 1:
        return <Step1Beneficiary {...commonProps} isSocietyLocked={isSocietyLocked} />;
      case 2:
        return <Step2VehicleInfo {...commonProps} />;
      case 3:
        return <Step3WarrantyConfiguration {...commonProps} />;
      case 4:
        return <Step4Payment {...commonProps} />;
      default:
        return <div>Étape non trouvée</div>;
    }
  };

  return (
    <>
      <WizardLayout
        steps={STEP_LABELS}
        currentStep={currentStep}
        completedSteps={touchedSteps}
        onStepClick={handleGoToStep}
        embedded={embedded}
        rightMargin={isPdfPreviewOpen ? pdfPanelWidth : 0}

        onPrevious={currentStep > 1 ? goToPreviousStep : undefined}
        onNext={handleNext}
        canProceed={canProceed}
        nextLabel={nextLabel}
        isSubmitting={createContract.isLoading}
        hideWhenTutorial={true}
        isTutorialActive={tutorialState.isActive}
        onSaveDraft={showDrafts ? handleSaveDraft : undefined}
        isSavingDraft={isSavingDraft}
        saveLabel={draftUuid ? 'Mettre à jour' : 'Sauvegarder'}
        helpAction={!tutorialState.isActive && !tutorialState.showIntro && isExternalUser && firstStepIndexForCurrentWizard !== -1 ? (
          <button
            type="button"
            onClick={() => { enableTutorials(); restartAtStep(firstStepIndexForCurrentWizard); }}
            className="tw-flex tw-items-center tw-gap-2 tw-px-3 sm:tw-px-5 tw-py-3 tw-bg-white tw-text-affinicar-primary tw-font-medium tw-rounded-xl tw-border tw-border-affinicar-lighter hover:tw-bg-affinicar-lighter tw-transition-colors"
            title="Relancer le tutoriel à partir de cette étape"
          >
            <HelpCircle className="tw-w-4 tw-h-4" />
            <span className="tw-hidden sm:tw-inline">Aide</span>
          </button>
        ) : undefined}
      >
        {renderCurrentStep()}
      </WizardLayout>

      {/* Panel de preview PDF HTML (toggle droit).
          L'identité affichée est celle du GARANT RÉSOLU via effectiveConfig.guarantorId
          (cohérent avec le backend `resolveDisplaySociety`). Si le garant pointe sur
          une autre société, c'est elle qui apparaît ; sinon, fallback sur la société
          émettrice. */}
      <PdfPreviewPanel
        formData={formData}
        warrantyName={warranty?.name}
        warrantyDuration={warranty?.duration}
        warrantyFinancialCap={warranty?.financialCap ? `${warranty.financialCap} €` : undefined}
        warrantyKmLimit={warranty?.kmLimit ? `${warranty.kmLimit.toLocaleString('fr-FR')} km` : undefined}
        warrantyType={effectiveWarrantyType}
        warrantyParts={warranty?.parts}
        currentStep={currentStep}
        guarantorName={displaySociety?.name}
        guarantorSiren={displaySociety?.siren || undefined}
        guarantorAddress={displaySociety?.address || undefined}
        guarantorCpCity={[displaySociety?.cp, displaySociety?.city].filter(Boolean).join(' ') || undefined}
        guarantorPhone={showPhoneInCgp ? (displaySociety?.phone || undefined) : undefined}
        guarantorEmail={displaySociety?.email || undefined}
        isIntermediary={formData.isIntermediary}
        logoUrl={displaySociety ? resolveLogoUrl(displaySociety) : null}
        paymentMethodText={paymentMethodText}
        assistanceEncart={assistanceEncart}
      />

      {/* Introduction du tutoriel (si activé dans le profil) — masqué si la modale vehicle check est ouverte */}
      {tutorialState.showIntro && isTutorialEnabled && !showVehicleCheckModal && (
        <TutorialIntro
          onStart={() => {
            if (effectiveConfig?.mandatoryVehicleCheck && !vehicleCheckDone) {
              setPendingAction('start_tutorial');
              setShowVehicleCheckModal(true);
              return;
            }
            startTutorial(firstStepIndexForCurrentWizard !== -1 ? firstStepIndexForCurrentWizard : 0);
          }}
          onSkip={() => {
            disableTutorials();
            if (effectiveConfig?.mandatoryVehicleCheck && !vehicleCheckDone) {
              setPendingAction('skip_tutorial');
              setShowVehicleCheckModal(true);
              return;
            }
            skipTutorial();
          }}
        />
      )}

      {/* Tutoriel interactif (si activé dans le profil) */}
      {tutorialState.isActive && currentTutorialStep && isTutorialEnabled && (
        <TutorialSpotlight
          currentStep={currentTutorialStep as any}
          stepNumber={tutorialState.currentStepIndex + 1}
          totalSteps={tutorialSteps.length}
          isFieldValid={isTutorialFieldValid}
          onNext={handleNextTutorialStep}
          onPrevious={handlePreviousTutorialStep}
          onSkip={() => { disableTutorials(); skipTutorial(); }}
          placement={currentTutorialStep.placement}
          onSaveDraft={handleSaveDraft}
          isSavingDraft={isSavingDraft}
          saveLabel={draftUuid ? 'Mettre à jour' : 'Sauvegarder'}
        />
      )}

      {/* Modale de vérification véhicule obligatoire */}
      {showVehicleCheckModal && (
        <VehicleCheckModal
          isOpen={showVehicleCheckModal}
          onConfirm={handleVehicleCheckConfirm}
          onCancel={onCancel || (() => navigate(returnTo || '/contracts'))}
          isUploading={isUploadingVehicleCheck}
        />
      )}

      {/* Modale de partage du lien de paiement (Stripe) */}
      {showPaymentModal && (
        <PaymentLinkModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            if (paymentLink) {
              if (onCreated) {
                onCreated(0);
              } else {
                navigate(returnTo || '/contracts');
              }
            }
          }}
          onConfirm={handlePaymentModalConfirm}
          defaultEmail={formData.beneficiary?.email || ''}
          defaultPhone={formData.beneficiary?.phoneNumber || ''}
          defaultSignatureType={formData.signatureType || 1}
          beneficiaryName={`${formData.beneficiary?.firstName || ''} ${formData.beneficiary?.lastName || ''}`.trim()}
          isSubmitting={createContract.isLoading}
          paymentLink={paymentLink}
          signatureLink={signatureLink}
          paymentContext={formData.paymentType === 2 ? 'monthly' : 'online'}
          defaultExpirationHours={effectiveConfig?.paymentLinkExpirationHours ?? 24}
          warrantyType={warranty?.warrantyType}
          guarantorName={guarantorId !== formData.societyId ? guarantorSociety?.name : undefined}
        />
      )}

      {/* Modale de confirmation (contrats sans paiement Stripe) */}
      {showConfirmationModal && (
        <ContractConfirmationModal
          isOpen={showConfirmationModal}
          onClose={() => setShowConfirmationModal(false)}
          onConfirm={handleConfirmationModalConfirm}
          defaultEmail={formData.beneficiary?.email || ''}
          defaultPhone={formData.beneficiary?.phoneNumber || ''}
          defaultSignatureType={formData.signatureType || 1}
          isSubmitting={createContract.isLoading}
          warrantyType={warranty?.warrantyType}
          assistanceSentence={confirmationAssistanceSentence}
          guarantorName={guarantorId !== formData.societyId ? guarantorSociety?.name : undefined}
        />
      )}
    </>
  );
}
